import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    X,
    User,
    Phone,
    Mail,
    MapPin,
    Banknote,
    Percent,
    FileDown,
    Loader2,
    Users,
    TrendingUp,
    ChevronDown,
    Pencil,
    Plus,
    Wallet,
    Save,
} from "lucide-react";
import { toast } from "sonner";
import { crud } from "../lib/api";
import { getEntity } from "../lib/entities";
import EntityForm from "./EntityForm";

const fmtMoney = (n) =>
    Number(n || 0).toLocaleString("tr-TR", {
        style: "currency",
        currency: "TRY",
        maximumFractionDigits: 2,
    });

const fmtDate = (s) => {
    if (!s) return "—";
    try {
        return new Date(s).toLocaleDateString("tr-TR");
    } catch {
        return String(s);
    }
};

/**
 * Helper: extract a foreign-key id from a record. Tries multiple common
 * field-name variants Django might serialize. Returns null if nothing
 * matches.
 */
function extractFkId(record, fieldNames) {
    if (!record) return null;
    for (const f of fieldNames) {
        const v = record[f];
        if (v === null || v === undefined) continue;
        if (typeof v === "object") return v?.id ?? null;
        return v;
    }
    return null;
}

const ARACI_FIELDS = ["araci", "araci_id", "aracı", "aracı_id"];
const MUVEKKIL_FIELDS = ["muvekkil", "muvekkil_id", "müvekkil", "müvekkil_id"];

function muvekkilHasAraci(record, araciId) {
    if (!record || araciId == null) return false;
    const aracilar = record.aracilar;
    if (Array.isArray(aracilar)) {
        return aracilar.some((item) => {
            if (item == null) return false;
            if (typeof item === "object") return idsEqual(item.id, araciId);
            return idsEqual(item, araciId);
        });
    }
    return idsEqual(extractFkId(record, ARACI_FIELDS), araciId);
}

function idsEqual(a, b) {
    if (a === null || a === undefined || b === null || b === undefined) return false;
    return a === b || String(a) === String(b);
}

/**
 * AraciDetailDrawer — slide-in detail panel for an Aracı record.
 *
 * Shows ONLY the Müvekkiller brought by this aracı with a collapsible
 * payments sub-list per müvekkil.
 */
export default function AraciDetailDrawer({ aracı, onClose }) {
    const [muvekkiller, setMuvekkiller] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pdfBusy, setPdfBusy] = useState(false);
    const [expanded, setExpanded] = useState({});
    const [paymentsByMuvekkil, setPaymentsByMuvekkil] = useState({});
    const [paymentsLoading, setPaymentsLoading] = useState({});
    const [diagnostics, setDiagnostics] = useState(null);

    const [crossEdit, setCrossEdit] = useState(null);
    const [crossEditLoading, setCrossEditLoading] = useState(false);
    const [crossEditRecord, setCrossEditRecord] = useState(null);

    /* ============== Load müvekkiller for this aracı ==============
     *
     * Multi-strategy approach:
     *   1) First fetch ALL müvekkiller (one call) and try to filter by
     *      the aracı field if the backend includes it in the serializer.
     *   2) If the field isn't there OR didn't filter properly, fall back
     *      to: fetch ödemeler with this aracı, extract unique muvekkil
     *      IDs from those ödemeler, and show only those müvekkiller.
     */
    const loadMuvekkiller = useCallback(async () => {
        if (!aracı?.id) return;
        setLoading(true);
        const targetId = aracı.id;
        const diag = { strategy: "unknown", received: 0, kept: 0, sample: null };

        try {
            // -------- Strategy A: filter by aracı on müvekkiller endpoint
            const muvData = await crud.list("muvekkiller", {
                araci: targetId,
                araci_id: targetId,
                page_size: 1000,
            });
            const muvItems = Array.isArray(muvData)
                ? muvData
                : muvData.results || [];
            diag.received = muvItems.length;
            diag.sample = muvItems[0] || null;

            // Does the response actually contain the aracı field on items?
            const hasAraciField = muvItems.some(
                (m) =>
                    extractFkId(m, ARACI_FIELDS) !== null ||
                    Array.isArray(m.aracilar),
            );

            if (hasAraciField) {
                const matched = muvItems.filter((m) =>
                    muvekkilHasAraci(m, targetId),
                );
                // If matched count looks reasonable, accept it
                if (matched.length < muvItems.length) {
                    diag.strategy = "muvekkil-field";
                    diag.kept = matched.length;
                    setMuvekkiller(matched);
                    setDiagnostics(diag);
                    return;
                }
                // If everything "matched", probably the field is bogus
                // (all items have same aracı ref). Fall through to B.
            }

            // -------- Strategy B: derive müvekkil ids from ödemeler
            const odData = await crud.list("odemeler", {
                araci: targetId,
                araci_id: targetId,
                page_size: 2000,
            });
            const odItems = Array.isArray(odData)
                ? odData
                : odData.results || [];

            // Strict filter on ödemeler by aracı (if field is present)
            const odHasAraci = odItems.some(
                (o) => extractFkId(o, ARACI_FIELDS) !== null,
            );
            const odForThisAraci = odHasAraci
                ? odItems.filter((o) =>
                      idsEqual(extractFkId(o, ARACI_FIELDS), targetId),
                  )
                : odItems; // trust backend filter

            // Collect unique muvekkil IDs
            const muvIdSet = new Set();
            for (const o of odForThisAraci) {
                const mid = extractFkId(o, MUVEKKIL_FIELDS);
                if (mid !== null && mid !== undefined) muvIdSet.add(String(mid));
            }

            // If we found müvekkil ids via ödemeler, intersect with full list
            if (muvIdSet.size > 0) {
                const matched = muvItems.filter((m) =>
                    muvIdSet.has(String(m.id)),
                );
                diag.strategy = "via-odemeler";
                diag.kept = matched.length;
                diag.muvekkilIdsFromOdeme = Array.from(muvIdSet);
                setMuvekkiller(matched);
                setDiagnostics(diag);
                return;
            }

            // -------- Strategy C: nothing worked → empty
            diag.strategy = "empty-fallback";
            diag.kept = 0;
            setMuvekkiller([]);
            setDiagnostics(diag);
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error("[AraciDetail] load failed", e);
            setMuvekkiller([]);
            setDiagnostics({ strategy: "error", error: String(e) });
        } finally {
            setLoading(false);
            // eslint-disable-next-line no-console
            console.log(`[AraciDetail] aracı=${targetId}`, diag);
        }
    }, [aracı?.id]);

    useEffect(() => {
        loadMuvekkiller();
    }, [loadMuvekkiller]);

    /* ============== Load payments for a single müvekkil ============== */
    const loadPaymentsFor = useCallback(async (muvekkilId) => {
        setPaymentsLoading((s) => ({ ...s, [muvekkilId]: true }));
        try {
            const data = await crud.list("odemeler", {
                muvekkil: muvekkilId,
                muvekkil_id: muvekkilId,
                page_size: 500,
            });
            const items = Array.isArray(data) ? data : data.results || [];

            const hasField = items.some(
                (p) => extractFkId(p, MUVEKKIL_FIELDS) !== null,
            );

            const filtered = !hasField
                ? items
                : items.filter((p) =>
                      idsEqual(extractFkId(p, MUVEKKIL_FIELDS), muvekkilId),
                  );

            setPaymentsByMuvekkil((s) => ({ ...s, [muvekkilId]: filtered }));
        } catch {
            setPaymentsByMuvekkil((s) => ({ ...s, [muvekkilId]: [] }));
        } finally {
            setPaymentsLoading((s) => ({ ...s, [muvekkilId]: false }));
        }
    }, []);

    function toggleExpand(muvekkilId) {
        const willOpen = !expanded[muvekkilId];
        setExpanded((s) => ({ ...s, [muvekkilId]: willOpen }));
        if (willOpen && !paymentsByMuvekkil[muvekkilId]) {
            loadPaymentsFor(muvekkilId);
        }
    }

    /* ============== PDF download ============== */
    async function downloadPdf() {
        setPdfBusy(true);
        try {
            await crud.openFile("aracilar", aracı.id, "pdf_rapor");
        } catch (e) {
            toast.error(
                e?.response?.data?.detail ||
                    e?.message ||
                    "PDF oluşturulamadı.",
            );
        } finally {
            setPdfBusy(false);
        }
    }

    async function openCrossEdit(next) {
        setCrossEdit(next);
        setCrossEditRecord(null);
        if (!next?.record?.id || next.entityKey !== "muvekkiller") {
            setCrossEditRecord(next?.record || null);
            setCrossEditLoading(false);
            return;
        }
        setCrossEditLoading(true);
        try {
            const full = await crud.retrieve("muvekkiller", next.record.id);
            setCrossEditRecord(full);
        } catch (e) {
            toast.error(
                e?.response?.data?.detail ||
                    e?.message ||
                    "Müvekkil detayı yüklenemedi.",
            );
            setCrossEdit(null);
        } finally {
            setCrossEditLoading(false);
        }
    }

    function closeCrossEdit() {
        setCrossEdit(null);
        setCrossEditRecord(null);
        setCrossEditLoading(false);
    }

    /* ============== Cross-edit submit handlers ============== */
    async function submitCross(payload) {
        if (!crossEdit) return;
        const { entityKey, record } = crossEdit;
        const entity = getEntity(entityKey);
        if (!entity) return;
        try {
            if (record?.id) {
                await crud.update(entity.resource, record.id, payload);
                toast.success(`${entity.label} güncellendi`);
            } else {
                await crud.create(entity.resource, payload);
                toast.success(`${entity.label} oluşturuldu`);
            }
            const m = crossEdit.muvekkilId;
            if (entityKey === "odemeler" && m) {
                loadPaymentsFor(m);
            }
            if (entityKey === "muvekkiller" || entityKey === "odemeler") {
                loadMuvekkiller();
            }
            closeCrossEdit();
        } catch (e) {
            throw e;
        }
    }

    /* ============== Totals ============== */
    const grandTotal = useMemo(
        () =>
            muvekkiller.reduce(
                (s, m) => s + (Number(m?.araciya_odenen_miktar) || 0),
                0,
            ),
        [muvekkiller],
    );

    if (!aracı) return null;

    const crossEntity = crossEdit ? getEntity(crossEdit.entityKey) : null;

    return (
        <div
            data-testid="araci-detail-drawer"
            className="fixed inset-0 z-50 flex justify-end"
        >
            <div
                className="absolute inset-0 bg-black/70 backdrop-blur-sm"
                onClick={onClose}
            />
            <div className="relative w-full sm:max-w-3xl h-full bg-[#121A2F] border-l border-white/10 shadow-2xl flex flex-col animate-fade-in overflow-hidden">
                {/* Header */}
                <div className="px-6 py-5 border-b border-white/10 flex items-start justify-between gap-4 bg-gradient-to-r from-[#D4AF37]/5 to-transparent">
                    <div className="flex items-center gap-4 min-w-0">
                        <div className="h-12 w-12 rounded-md bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center shrink-0">
                            <User
                                className="h-5 w-5 text-[#F3E5AB]"
                                strokeWidth={1.8}
                            />
                        </div>
                        <div className="min-w-0">
                            <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37]">
                                Aracı Detayı
                            </div>
                            <h2 className="font-display text-2xl text-white truncate">
                                {aracı.ad_soyad ||
                                    [aracı.ad, aracı.soyad]
                                        .filter(Boolean)
                                        .join(" ") ||
                                    `#${aracı.id}`}
                            </h2>
                            {aracı.firma && (
                                <div className="text-xs text-slate-400 mt-0.5 truncate">
                                    {aracı.firma}
                                </div>
                            )}
                        </div>
                    </div>
                    <button
                        data-testid="araci-detail-close"
                        onClick={onClose}
                        className="h-9 w-9 rounded-md hover:bg-white/5 flex items-center justify-center text-slate-400 hover:text-white shrink-0"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto">
                    <div className="px-6 py-5 grid grid-cols-2 lg:grid-cols-4 gap-3 border-b border-white/10">
                        <InfoCard
                            icon={Phone}
                            label="Telefon"
                            value={aracı.telefon}
                            href={
                                aracı.telefon
                                    ? `tel:${aracı.telefon}`
                                    : null
                            }
                        />
                        <InfoCard
                            icon={Mail}
                            label="E-posta"
                            value={aracı.email}
                            href={
                                aracı.email
                                    ? `mailto:${aracı.email}`
                                    : null
                            }
                        />
                        <InfoCard
                            icon={Banknote}
                            label="IBAN"
                            value={aracı.iban}
                        />
                        <InfoCard
                            icon={MapPin}
                            label="Şehir"
                            value={aracı.sehir}
                        />
                        <InfoCard
                            icon={Percent}
                            label="Komisyon"
                            value={
                                aracı.komisyon_orani != null
                                    ? `%${aracı.komisyon_orani}`
                                    : null
                            }
                        />
                    </div>

                    {/* Müvekkiller */}
                    <div className="px-6 py-5">
                        <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                                <Users className="h-4 w-4 text-[#F3E5AB]" />
                                <h3 className="font-display text-lg text-white">
                                    Bağlı Müvekkiller
                                </h3>
                            </div>
                            <div className="text-xs text-slate-400 flex items-center gap-3">
                                {diagnostics?.strategy && (
                                    <span
                                        className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-white/5 border border-white/10 text-slate-500"
                                        title="Hangi strateji ile bulundu"
                                    >
                                        {diagnostics.strategy}
                                    </span>
                                )}
                                <span>
                                    Toplam{" "}
                                    <span className="font-mono-jb text-slate-200">
                                        {muvekkiller.length}
                                    </span>{" "}
                                    müvekkil
                                </span>
                            </div>
                        </div>

                        {loading ? (
                            <div className="py-8 text-center text-slate-500 text-sm flex items-center justify-center gap-2">
                                <Loader2 className="h-4 w-4 animate-spin" />
                                Yükleniyor...
                            </div>
                        ) : muvekkiller.length === 0 ? (
                            <div className="py-8 text-center text-slate-500 text-sm border border-dashed border-white/10 rounded-md">
                                Bu aracıya bağlı müvekkil bulunmuyor.
                            </div>
                        ) : (
                            <ul className="space-y-2">
                                {muvekkiller.map((m) => (
                                    <MuvekkilCard
                                        key={m.id}
                                        muvekkil={m}
                                        aracı={aracı}
                                        expanded={!!expanded[m.id]}
                                        onToggle={() => toggleExpand(m.id)}
                                        payments={
                                            paymentsByMuvekkil[m.id] || []
                                        }
                                        loading={!!paymentsLoading[m.id]}
                                        onEditMuvekkil={() =>
                                            openCrossEdit({
                                                entityKey: "muvekkiller",
                                                record: m,
                                            })
                                        }
                                        onEditPayment={(p) =>
                                            setCrossEdit({
                                                entityKey: "odemeler",
                                                record: p,
                                                muvekkilId: m.id,
                                            })
                                        }
                                        onAddPayment={() =>
                                            setCrossEdit({
                                                entityKey: "odemeler",
                                                record: {
                                                    muvekkil: m.id,
                                                    araci: aracı.id,
                                                },
                                                muvekkilId: m.id,
                                            })
                                        }
                                        onMuvekkilUpdated={loadMuvekkiller}
                                    />
                                ))}
                            </ul>
                        )}

                        {muvekkiller.length > 0 && (
                            <div className="mt-4 pt-4 border-t border-white/10 flex items-center justify-between bg-gradient-to-r from-[#D4AF37]/5 to-transparent rounded-md px-3 py-3">
                                <div className="flex items-center gap-2">
                                    <TrendingUp className="h-4 w-4 text-[#F3E5AB]" />
                                    <div>
                                        <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">
                                            Toplam Aracıya Ödenen
                                        </div>
                                        <div className="text-xs text-slate-400">
                                            {muvekkiller.length} müvekkil
                                            üzerinden
                                        </div>
                                    </div>
                                </div>
                                <div
                                    data-testid="araci-toplam-odenen"
                                    className="font-display text-2xl gold-text font-mono-jb"
                                >
                                    {fmtMoney(grandTotal)}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-white/10 flex items-center justify-between gap-3 bg-[#0B0F19]/40">
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                        <Banknote className="h-3.5 w-3.5" />
                        <span>
                            Aracı ID:{" "}
                            <span className="font-mono-jb text-slate-300">
                                #{aracı.id}
                            </span>
                        </span>
                    </div>
                    <button
                        data-testid="araci-pdf-button"
                        onClick={downloadPdf}
                        disabled={pdfBusy}
                        className="btn-gold h-10 px-4 rounded-md text-sm inline-flex items-center gap-2 disabled:opacity-60"
                    >
                        {pdfBusy ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            <FileDown className="h-4 w-4" />
                        )}
                        PDF Rapor İndir
                    </button>
                </div>
            </div>

            {/* Cross-navigation edit modal (stacks on top of drawer) */}
            {crossEdit && crossEntity && (
                <EntityForm
                    open
                    loading={crossEditLoading}
                    title={
                        crossEdit.record?.id
                            ? `${crossEntity.label} Düzenle`
                            : `Yeni ${crossEntity.label}`
                    }
                    fields={crossEntity.fields || []}
                    initial={crossEditRecord ?? crossEdit.record}
                    resource={crossEntity.resource}
                    schema={crossEntity.schema}
                    formFieldsStrict={crossEntity.formFieldsStrict}
                    onClose={closeCrossEdit}
                    onSubmit={submitCross}
                />
            )}
        </div>
    );
}

function MuvekkilCard({
    muvekkil,
    expanded,
    onToggle,
    payments,
    loading,
    onEditMuvekkil,
    onEditPayment,
    onAddPayment,
    onMuvekkilUpdated,
}) {
    const fullName =
        [muvekkil.ad, muvekkil.soyad].filter(Boolean).join(" ") ||
        muvekkil.unvan ||
        muvekkil.ad_soyad ||
        `#${muvekkil.id}`;

    const officialAmount = Number(muvekkil?.araciya_odenen_miktar) || 0;
    const computedAmount = useMemo(
        () => payments.reduce((s, p) => s + (Number(p?.tutar) || 0), 0),
        [payments],
    );

    const [draftAmount, setDraftAmount] = useState(
        officialAmount ? String(officialAmount) : "",
    );
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setDraftAmount(officialAmount ? String(officialAmount) : "");
    }, [officialAmount]);

    const numericDraft = draftAmount === "" ? 0 : Number(draftAmount);
    const dirty =
        !Number.isNaN(numericDraft) && numericDraft !== officialAmount;

    async function saveAmount() {
        if (!dirty || saving) return;
        setSaving(true);
        try {
            await crud.update("muvekkiller", muvekkil.id, {
                araciya_odenen_miktar: numericDraft,
            });
            toast.success("Aracıya ödenen miktar güncellendi");
            onMuvekkilUpdated?.();
        } catch (e) {
            toast.error(
                e?.response?.data?.detail ||
                    e?.response?.data?.araciya_odenen_miktar?.[0] ||
                    e?.message ||
                    "Güncelleme başarısız.",
            );
        } finally {
            setSaving(false);
        }
    }

    return (
        <li
            data-testid={`araci-muvekkil-${muvekkil.id}`}
            className="rounded-md border border-white/10 bg-[#0B0F19] hover:border-white/20 transition overflow-hidden"
        >
            <div className="flex items-center gap-3 px-3 py-2.5">
                <button
                    type="button"
                    onClick={onToggle}
                    className="h-8 w-8 rounded hover:bg-white/5 flex items-center justify-center text-slate-400 shrink-0"
                    title={expanded ? "Daralt" : "Genişlet"}
                >
                    <ChevronDown
                        className={`h-4 w-4 transition-transform ${
                            expanded ? "" : "-rotate-90"
                        }`}
                    />
                </button>

                <div className="min-w-0 flex-1">
                    <div className="text-sm text-slate-100 truncate">
                        {fullName}
                    </div>
                    <div className="flex items-center gap-3 mt-0.5">
                        {muvekkil.tc_no && (
                            <span className="text-[10px] text-slate-500 font-mono-jb">
                                TC: {muvekkil.tc_no}
                            </span>
                        )}
                        {muvekkil.telefon && (
                            <span className="text-[10px] text-slate-500">
                                {muvekkil.telefon}
                            </span>
                        )}
                    </div>
                </div>

                <div className="shrink-0 flex items-center gap-1.5">
                    <div className="hidden sm:block text-right">
                        <div className="text-[9px] uppercase tracking-[0.14em] text-slate-500 leading-tight">
                            Aracıya Ödenen ₺
                        </div>
                    </div>
                    <input
                        type="number"
                        step="any"
                        min="0"
                        value={draftAmount}
                        onChange={(e) => setDraftAmount(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") saveAmount();
                        }}
                        data-testid={`muvekkil-amount-${muvekkil.id}`}
                        placeholder="0"
                        className={`w-28 bg-[#121A2F] border rounded-md px-2 py-1.5 text-sm text-right font-mono-jb tabular-nums focus:ring-1 focus:ring-[#D4AF37] outline-none transition ${
                            dirty
                                ? "border-[#D4AF37] text-[#F3E5AB]"
                                : "border-white/10 text-slate-200"
                        }`}
                    />
                    <button
                        type="button"
                        onClick={saveAmount}
                        disabled={!dirty || saving}
                        data-testid={`muvekkil-amount-save-${muvekkil.id}`}
                        title={dirty ? "Kaydet" : "Değişiklik yok"}
                        className={`h-8 px-2 rounded-md text-[11px] inline-flex items-center gap-1 transition ${
                            dirty
                                ? "bg-[#D4AF37]/15 hover:bg-[#D4AF37]/25 border border-[#D4AF37]/40 text-[#F3E5AB]"
                                : "bg-white/5 border border-white/10 text-slate-600 cursor-not-allowed"
                        }`}
                    >
                        {saving ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                            <Save className="h-3 w-3" />
                        )}
                        Update
                    </button>
                </div>

                <button
                    type="button"
                    data-testid={`muvekkil-edit-${muvekkil.id}`}
                    onClick={onEditMuvekkil}
                    title="Müvekkili Düzenle"
                    className="h-8 w-8 rounded-md hover:bg-white/5 flex items-center justify-center text-slate-400 hover:text-[#F3E5AB] transition shrink-0"
                >
                    <Pencil className="h-4 w-4" />
                </button>
            </div>

            {expanded && (
                <div className="border-t border-white/10 px-3 py-3 bg-[#0F1626]">
                    <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                            <Wallet className="h-3.5 w-3.5 text-[#F3E5AB]" />
                            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">
                                Ödemeler
                            </div>
                        </div>
                        <button
                            type="button"
                            data-testid={`add-payment-${muvekkil.id}`}
                            onClick={onAddPayment}
                            className="h-7 px-2.5 rounded-md bg-[#D4AF37]/10 hover:bg-[#D4AF37]/20 border border-[#D4AF37]/30 text-[#F3E5AB] text-[11px] inline-flex items-center gap-1.5"
                        >
                            <Plus className="h-3 w-3" /> Yeni Ödeme
                        </button>
                    </div>

                    {loading ? (
                        <div className="py-3 text-center text-slate-500 text-xs flex items-center justify-center gap-2">
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            Ödemeler yükleniyor...
                        </div>
                    ) : payments.length === 0 ? (
                        <div className="py-2 text-center text-slate-600 text-xs italic">
                            Bu müvekkile ait ödeme kaydı yok.
                        </div>
                    ) : (
                        <ul className="space-y-1.5">
                            {payments.map((p) => (
                                <li
                                    key={p.id}
                                    data-testid={`payment-row-${p.id}`}
                                    className="flex items-center gap-3 px-2.5 py-2 rounded border border-white/5 bg-[#0B0F19] hover:border-white/15 transition"
                                >
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2">
                                            <span className="font-mono-jb text-sm text-[#F3E5AB] tabular-nums">
                                                {fmtMoney(p.tutar)}
                                            </span>
                                            {p.para_birimi &&
                                                p.para_birimi !== "TRY" && (
                                                    <span className="text-[9px] uppercase tracking-wider text-slate-500">
                                                        {p.para_birimi}
                                                    </span>
                                                )}
                                            {p.odeme_durumu && (
                                                <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-white/5 border border-white/10 text-slate-300">
                                                    {p.odeme_durumu}
                                                </span>
                                            )}
                                        </div>
                                        <div className="text-[11px] text-slate-500 mt-0.5 truncate">
                                            {fmtDate(p.odeme_tarihi)}
                                            {p.odeme_yontemi && (
                                                <>
                                                    {" · "}
                                                    {p.odeme_yontemi}
                                                </>
                                            )}
                                            {p.aciklama && (
                                                <>
                                                    {" · "}
                                                    {String(p.aciklama).slice(
                                                        0,
                                                        60,
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => onEditPayment(p)}
                                        data-testid={`payment-edit-${p.id}`}
                                        title="Ödemeyi Düzenle"
                                        className="h-7 px-2 rounded-md bg-white/5 hover:bg-[#D4AF37]/15 border border-white/10 hover:border-[#D4AF37]/40 text-slate-300 hover:text-[#F3E5AB] transition inline-flex items-center gap-1 text-[11px] font-medium"
                                    >
                                        <Pencil className="h-3 w-3" />
                                        Düzenle
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}

                    {payments.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-white/5 flex items-center justify-between text-[11px]">
                            <span className="text-slate-500">
                                Ödeme toplamı ({payments.length})
                            </span>
                            <span className="font-mono-jb text-slate-200 tabular-nums">
                                {fmtMoney(computedAmount)}
                            </span>
                        </div>
                    )}
                </div>
            )}
        </li>
    );
}

function InfoCard({ icon: Icn, label, value, href }) {
    const content = (
        <div className="rounded-md border border-white/10 bg-[#0B0F19] p-3 hover:border-white/20 transition h-full">
            <div className="flex items-center gap-2 mb-1.5">
                <Icn className="h-3.5 w-3.5 text-slate-500" />
                <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">
                    {label}
                </div>
            </div>
            <div
                className={`text-sm ${
                    value
                        ? href
                            ? "text-[#F3E5AB]"
                            : "text-slate-100"
                        : "text-slate-600"
                } truncate`}
            >
                {value || "—"}
            </div>
        </div>
    );
    return href && value ? (
        <a href={href} className="block">
            {content}
        </a>
    ) : (
        content
    );
}
